const CSS_PIXELS_PER_INCH = 96;
const WEB_BODY_FONT_SIZE_PT = 11;

/** Returns whether a numeric metadata value can participate in a sizing calculation. */
function isPositiveFinite(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Calculates the uncapped display width for a PDF Builder image in a listing card.
 * The caller caps the result at the card width so wide creatives cannot overflow.
 *
 * @param naturalImageWidth - Intrinsic image width reported by the browser, in pixels.
 * @param baselineFontSizePt - Tenant default classified font size, in points.
 * @param renderDpi - DPI used when the PDF Builder document was rendered.
 * @returns The calibrated CSS width in pixels, or null when calibration metadata is invalid.
 */
export function calculatePdfBuilderCardWidth(
  naturalImageWidth: number,
  baselineFontSizePt: number | null | undefined,
  renderDpi: number | null | undefined,
): number | null {
  if (
    !isPositiveFinite(naturalImageWidth)
    || !isPositiveFinite(baselineFontSizePt)
    || !isPositiveFinite(renderDpi)
  ) {
    return null;
  }

  const readabilityBias = Math.max(1, WEB_BODY_FONT_SIZE_PT / baselineFontSizePt);
  const displayWidth = naturalImageWidth * (CSS_PIXELS_PER_INCH / renderDpi) * readabilityBias;

  return isPositiveFinite(displayWidth) ? displayWidth : null;
}
